import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Heart, Star, MessageSquare, TrendingUp, User, Settings, CreditCard, Check } from 'lucide-react';
import { SubscriptionStatus } from '@/components/SubscriptionStatus';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
// import { BotCard } from '@/components/BotCard';

interface Profile {
  id: string;
  user_id: string;
  username: string | null;
  email: string | null;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  website: string | null;
  is_developer: boolean;
}

interface UserStats {
  favoriteCount: number;
  reviewCount: number;
  submissionCount: number;
  averageRating: number;
}

interface DashboardBot {
  id: string;
  name: string;
  emoji: string;
  description: string;
  rating: number;
  pricing: string;
  website_url: string;
  category?: string;
  reviewCount?: number;
  price?: string;
  features?: string[];
  model_type?: string;
  language?: string;
  use_case?: string;
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [stats, setStats] = useState<UserStats>({
    favoriteCount: 0,
    reviewCount: 0,
    submissionCount: 0,
    averageRating: 0
  });
  const [favoriteBots, setFavoriteBots] = useState<DashboardBot[]>([]);
  const [userReviews, setUserReviews] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    username: '',
    bio: '',
    location: '',
    website: ''
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
      return;
    }

    if (user) {
      fetchProfile();
      fetchStats();
      fetchFavoriteBots();
      fetchUserReviews();
    }
  }, [user, loading, navigate]);

  // Handle URL tab parameter changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setProfile(data);
        setProfileForm({
          username: data.username || '',
          bio: data.bio || '',
          location: data.location || '',
          website: data.website || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchStats = async () => {
    if (!user) return;

    try {
      // Fetch favorite count
      const { count: favoriteCount } = await supabase
        .from('user_favorites')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Fetch review count and average rating
      const { data: reviews } = await supabase
        .from('bot_ratings')
        .select('rating')
        .eq('user_id', user.id);

      // Fetch submission count
      const { count: submissionCount } = await supabase
        .from('bot_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      const reviewCount = reviews?.length || 0;
      const averageRating = reviewCount > 0 
        ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviewCount 
        : 0;

      setStats({
        favoriteCount: favoriteCount || 0,
        reviewCount,
        submissionCount: submissionCount || 0,
        averageRating: Math.round(averageRating * 10) / 10
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFavoriteBots = async () => {
    if (!user) return;

    try {
      // First get the bot IDs from favorites
      const { data: favorites } = await supabase
        .from('user_favorites')
        .select('bot_id')
        .eq('user_id', user.id);

      if (favorites && favorites.length > 0) {
        const botIds = favorites.map(f => f.bot_id);
        
        // Then get the bot details
        const { data: bots } = await supabase
          .from('bots')
          .select('id, name, emoji, description, rating, pricing, website_url, model_type, language, use_case')
          .in('id', botIds)
          .limit(6);

        if (bots) {
          setFavoriteBots(bots.map(bot => ({
            ...bot,
            category: bot.use_case || '',
            reviewCount: 0,
            price: bot.pricing,
            features: [],
            tags: [],
            popularity: 0,
            imageUrl: '',
            website: bot.website_url || ''
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching favorite bots:', error);
    }
  };

  const fetchUserReviews = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('bot_ratings')
        .select(`
          *,
          bots (name, emoji)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      setUserReviews(data || []);
    } catch (error) {
      console.error('Error fetching user reviews:', error);
    }
  };

  const updateProfile = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          email: user.email,
          username: profileForm.username,
          bio: profileForm.bio,
          location: profileForm.location,
          website: profileForm.website
        });

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated."
      });

      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Recently viewed tools from localStorage
  const recentlyViewed = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('vergo-recently-viewed');
      return stored ? JSON.parse(stored).slice(0, 4) : [];
    } catch { return []; }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={profile?.avatar_url || ''} />
              <AvatarFallback className="text-lg">
                {profile?.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-3xl font-bold">
                {profile?.username || user.email?.split('@')[0] || 'User'}
              </h1>
              <p className="text-muted-foreground">{user.email}</p>
              {profile?.bio && (
                <p className="text-sm mt-1 max-w-md">{profile.bio}</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-4 ml-auto">
            <Button 
              variant="outline" 
              onClick={() => setIsEditing(!isEditing)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Favorites</CardTitle>
              <Heart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.favoriteCount}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Reviews</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.reviewCount}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Submissions</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.submissionCount}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Rating</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.averageRating > 0 ? stats.averageRating : '-'}
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="favorites">Favorites</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
            {isEditing && <TabsTrigger value="settings">Settings</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Quick-Start AI Templates */}
            <Card>
              <CardHeader>
                <CardTitle>Quick-Start Templates</CardTitle>
                <CardDescription>Jump into common AI workflows</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { emoji: '📝', title: 'Summarize Text', desc: 'Paste any text and get a concise summary' },
                    { emoji: '🔍', title: 'Deep Research', desc: 'Find answers with AI-powered search' },
                    { emoji: '💻', title: 'Code Assistant', desc: 'Get help writing and debugging code' },
                  ].map((t, i) => (
                    <div key={i} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer">
                      <span className="text-2xl">{t.emoji}</span>
                      <h4 className="font-medium mt-2">{t.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{t.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recently Viewed */}
            {recentlyViewed.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Recently Viewed</CardTitle>
                  <CardDescription>Pick up where you left off</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {recentlyViewed.map((tool: any) => (
                      <div key={tool.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                        <span className="text-xl">{tool.emoji || '🤖'}</span>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate text-sm">{tool.name}</h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Favorites */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Favorites</CardTitle>
                  <CardDescription>Your latest favorite AI bots</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {favoriteBots.length > 0 ? (
                      favoriteBots.slice(0, 3).map((bot) => (
                        <div key={bot.id} className="flex items-center gap-3 p-3 rounded-lg border">
                          <span className="text-2xl">{bot.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate">{bot.name}</h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {bot.description}
                            </p>
                          </div>
                          <Badge variant="secondary">{bot.pricing}</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No favorites yet. Start exploring AI bots!
                      </p>
                    )}
                  </div>
                  {favoriteBots.length > 3 && (
                    <Button variant="outline" className="w-full mt-4">
                      View All Favorites
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Recent Reviews */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Reviews</CardTitle>
                  <CardDescription>Your latest bot reviews</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {userReviews.length > 0 ? (
                      userReviews.slice(0, 3).map((review) => (
                        <div key={review.id} className="p-3 rounded-lg border space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{review.bots?.emoji}</span>
                              <span className="font-medium">{review.bots?.name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          {review.comment && (
                            <p className="text-sm text-muted-foreground">
                              {review.comment}
                            </p>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No reviews yet. Try some AI bots and share your thoughts!
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="favorites">
            <Card>
              <CardHeader>
                <CardTitle>Your Favorite Bots</CardTitle>
                <CardDescription>All the AI bots you've saved as favorites</CardDescription>
              </CardHeader>
              <CardContent>
                {favoriteBots.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {favoriteBots.map((bot) => (
                      <div key={bot.id} className="p-4 rounded-lg border space-y-3 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{bot.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">{bot.name}</h3>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                <span className="text-sm text-muted-foreground">{bot.rating}</span>
                              </div>
                              <Badge variant="secondary">{bot.pricing}</Badge>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {bot.description}
                        </p>
                        {bot.website_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => window.open(bot.website_url, '_blank')}
                          >
                            Visit Website
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No favorites yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Start exploring and save your favorite AI bots
                    </p>
                    <Button onClick={() => navigate('/')}>
                      Explore AI Bots
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle>Your Reviews</CardTitle>
                <CardDescription>All the reviews you've written for AI bots</CardDescription>
              </CardHeader>
              <CardContent>
                {userReviews.length > 0 ? (
                  <div className="space-y-4">
                    {userReviews.map((review) => (
                      <div key={review.id} className="p-4 rounded-lg border space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{review.bots?.emoji}</span>
                            <div>
                              <h4 className="font-medium">{review.bots?.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        {review.comment && (
                          <p className="text-sm">{review.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No reviews yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Try some AI bots and share your experience
                    </p>
                    <Button onClick={() => navigate('/')}>
                      Explore AI Bots
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription">
            <div className="space-y-6">
              {/* Subscription Status */}
              <SubscriptionStatus />
              
              {/* Subscription Plans */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Choose Your Plan
                  </CardTitle>
                  <CardDescription>
                    Select the plan that best fits your needs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <SubscriptionCard 
                      plan={{
                        id: 'basic',
                        name: 'Basic',
                        price: '$9.99',
                        description: 'Perfect for getting started with AI bots',
                        icon: <Check className="h-5 w-5" />,
                        features: [
                          'Access to 50+ AI bots',
                          'Basic analytics',
                          'Email support',
                          'Standard features'
                        ]
                      }}
                    />
                    <SubscriptionCard 
                      plan={{
                        id: 'premium',
                        name: 'Premium',
                        price: '$19.99',
                        description: 'Best for power users and professionals',
                        icon: <Star className="h-5 w-5" />,
                        popular: true,
                        features: [
                          'Access to 200+ AI bots',
                          'Advanced analytics',
                          'Priority support',
                          'Premium features',
                          'AI recommendations',
                          'Custom integrations'
                        ]
                      }}
                    />
                    <SubscriptionCard 
                      plan={{
                        id: 'enterprise',
                        name: 'Enterprise',
                        price: '$49.99',
                        description: 'For teams and organizations',
                        icon: <CreditCard className="h-5 w-5" />,
                        features: [
                          'Unlimited AI bots',
                          'Team management',
                          'White-label options',
                          'Custom development',
                          'Dedicated support',
                          'SLA guarantee'
                        ]
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {isEditing && (
            <TabsContent value="settings">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Settings</CardTitle>
                  <CardDescription>Update your profile information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        value={profileForm.username}
                        onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                        placeholder="Enter your username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={profileForm.location}
                        onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                        placeholder="Enter your location"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={profileForm.website}
                      onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                      placeholder="Enter your website URL"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      placeholder="Tell us about yourself"
                      rows={4}
                    />
                  </div>
                  <div className="flex gap-4">
                    <Button onClick={updateProfile}>
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}